import {
  getPdfJs,
  type PDFDocumentProxy,
} from 'features/knowledgeDocumentViewer/utils/loadPdfDocument'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

type PdfImageResult = {
  imageUrl: string | null
  width: number
  height: number
  isLoading: boolean
  error: boolean
  stale: boolean
  reload: () => void
}

export type PdfImageState = {
  key: string | null
  imageUrl: string | null
  width: number
  height: number
  isLoading: boolean
  error: boolean
}

const IDLE_PDF_IMAGE: PdfImageState = {
  key: null,
  imageUrl: null,
  width: 0,
  height: 0,
  isLoading: false,
  error: false,
}

/**
 * The hook's view of its state for the page it is currently asked for. State
 * left over from another page or scale is not loading for this one, so a new
 * request reads as loading from its first render rather than after the effect.
 */
export function resolvePdfImageView(
  state: PdfImageState,
  currentKey: string | null,
  enabled: boolean
): Omit<PdfImageResult, 'reload'> {
  const isCurrent = state.key === currentKey
  return {
    imageUrl: state.imageUrl,
    width: state.width,
    height: state.height,
    isLoading: isCurrent ? state.isLoading : enabled && currentKey !== null,
    error: isCurrent && state.error,
    stale: !isCurrent && state.imageUrl !== null,
  }
}

type PageCacheEntry = {
  blobUrl: string
  width: number
  height: number
}

export type UsePdfAsImageOptions = {
  /** When false, only the requested page is rendered (list cards). Default true. */
  preloadAdjacentPages?: boolean
  /**
   * After the requested page is rendered, drop the PDF document proxy but keep
   * the page image blob. Saves memory for list fallbacks while preserving
   * instant remounts across search pagination.
   */
  releaseDocumentAfterRender?: boolean
}

// Multi-document caches so N list-card PDF fallbacks can load without
// abandoning sibling getDocument tasks. Page blobs persist across unmount so
// search pagination can reuse rendered thumbs.

const DOC_CACHE_MAX = 24
const PAGE_CACHE_MAX = 160
const DOC_SWITCH_DEBOUNCE_MS = 150
const MAX_CONCURRENT_PDF_WORK = 3

const docCache = new Map<string, PDFDocumentProxy>()
const docLoading = new Map<string, Promise<PDFDocumentProxy>>()
const pageCache = new Map<string, PageCacheEntry>()

let activePdfWork = 0
const pdfWorkQueue: Array<() => void> = []

function acquirePdfWork(): Promise<void> {
  if (activePdfWork < MAX_CONCURRENT_PDF_WORK) {
    activePdfWork += 1
    return Promise.resolve()
  }
  return new Promise((resolve) => {
    pdfWorkQueue.push(() => {
      activePdfWork += 1
      resolve()
    })
  })
}

function releasePdfWork() {
  activePdfWork = Math.max(0, activePdfWork - 1)
  const next = pdfWorkQueue.shift()
  if (next) next()
}

function pageCacheKey(identity: string, page: number, scale: number) {
  return `${identity}:${page}:${scale}`
}

function evictPageCache() {
  while (pageCache.size > PAGE_CACHE_MAX) {
    const oldest = pageCache.keys().next().value
    if (!oldest) break
    const entry = pageCache.get(oldest)
    if (entry) URL.revokeObjectURL(entry.blobUrl)
    pageCache.delete(oldest)
  }
}

function evictDocCache() {
  while (docCache.size > DOC_CACHE_MAX) {
    const oldest = docCache.keys().next().value
    if (!oldest) break
    const doc = docCache.get(oldest)
    docCache.delete(oldest)
    if (doc) safeDestroyDoc(doc)
  }
}

function safeDestroyDoc(doc: PDFDocumentProxy) {
  setTimeout(() => doc.destroy().catch(() => {}), 0)
}

function releaseDocument(identity: string) {
  const doc = docCache.get(identity)
  if (!doc) return
  docCache.delete(identity)
  safeDestroyDoc(doc)
}

async function getPdfDocument(
  url: string,
  identity: string
): Promise<PDFDocumentProxy> {
  const cached = docCache.get(identity)
  if (cached) {
    return cached
  }
  const pending = docLoading.get(identity)
  if (pending) {
    return pending
  }

  const pdfjsLib = await getPdfJs()
  const loadingTask = pdfjsLib.getDocument({ url, verbosity: 0 })
  const promise = loadingTask.promise
    .then((pdf) => {
      docCache.set(identity, pdf)
      docLoading.delete(identity)
      evictDocCache()
      return pdf
    })
    .catch((error: unknown) => {
      docLoading.delete(identity)
      throw error
    })
  docLoading.set(identity, promise)
  return promise
}

async function renderPage(
  pdf: PDFDocumentProxy,
  pageNum: number,
  scale: number,
  identity: string,
  isCancelled?: () => boolean
): Promise<PageCacheEntry | null> {
  const key = pageCacheKey(identity, pageNum, scale)
  const cached = pageCache.get(key)
  if (cached) {
    // Refresh LRU order
    pageCache.delete(key)
    pageCache.set(key, cached)
    return cached
  }

  const page = await pdf.getPage(pageNum)
  if (isCancelled?.()) return null

  const viewport = page.getViewport({ scale })
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Could not get canvas 2d context')

  canvas.height = viewport.height
  canvas.width = viewport.width

  await page.render({ canvasContext: context, viewport, canvas }).promise

  if (isCancelled?.()) {
    canvas.width = 0
    canvas.height = 0
    return null
  }

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('toBlob failed'))),
      'image/jpeg',
      0.85
    )
  })

  canvas.width = 0
  canvas.height = 0

  if (isCancelled?.()) return null

  const entry: PageCacheEntry = {
    blobUrl: URL.createObjectURL(blob),
    width: viewport.width,
    height: viewport.height,
  }
  pageCache.set(key, entry)
  evictPageCache()
  return entry
}

/**
 * Hook that converts a PDF to an image URL using canvas.
 * Caches rendered page blobs at module level across unmount so search
 * pagination can reuse list-card PDF fallbacks.
 */
export function usePdfAsImage(
  pdfUrl: string | undefined,
  pageNumber = 1,
  scale = 1.5,
  totalPages = 1,
  enabled = true,
  options?: UsePdfAsImageOptions
): PdfImageResult {
  const preloadAdjacentPages = options?.preloadAdjacentPages !== false
  const releaseDocumentAfterRender =
    options?.releaseDocumentAfterRender === true

  const [result, setResult] = useState<PdfImageState>(IDLE_PDF_IMAGE)
  const [attempt, setAttempt] = useState(0)
  const currentKeyRef = useRef<string | null>(null)
  const cancellationRetriedKeyRef = useRef<string | null>(null)
  // The cached blob is dropped, not revoked: another instance may still show it.
  const reload = useCallback(() => {
    const key = currentKeyRef.current
    if (key) pageCache.delete(key)
    setAttempt((count) => count + 1)
  }, [])

  const displayedUrlRef = useRef<string | null>(null)

  const pdfIdentity = useMemo(() => {
    if (!pdfUrl) return null
    try {
      const url = new URL(pdfUrl)
      return url.origin + url.pathname
    } catch {
      return pdfUrl
    }
  }, [pdfUrl])

  const latestPdfUrlRef = useRef(pdfUrl)
  latestPdfUrlRef.current = pdfUrl

  const prevIdentityRef = useRef(pdfIdentity)

  const loadPage = useCallback(
    async (mounted: () => boolean, identity: string) => {
      const isCancelled = () => !mounted()
      await acquirePdfWork()
      try {
        const pdf = await getPdfDocument(latestPdfUrlRef.current!, identity)
        if (!mounted()) return

        const entry = await renderPage(pdf, pageNumber, scale, identity)
        if (!mounted() || !entry) return

        displayedUrlRef.current = entry.blobUrl
        setResult({
          key: pageCacheKey(identity, pageNumber, scale),
          imageUrl: entry.blobUrl,
          width: entry.width,
          height: entry.height,
          isLoading: false,
          error: false,
        })

        if (releaseDocumentAfterRender) {
          releaseDocument(identity)
          return
        }

        if (!preloadAdjacentPages) return

        const toPreload = [
          pageNumber + 1,
          pageNumber + 2,
          pageNumber - 1,
        ].filter((p) => p >= 1 && p <= totalPages)
        await Promise.allSettled(
          toPreload.map((p) =>
            mounted()
              ? renderPage(pdf, p, scale, identity, isCancelled)
              : Promise.resolve(null)
          )
        )
      } catch (error) {
        const msg = (error as Error)?.message ?? ''
        const isCancellation =
          msg.includes('Rendering cancelled') ||
          msg.includes('Worker was destroyed') ||
          msg.includes('Transport destroyed')
        if (!isCancellation) {
          console.error('Error converting PDF to image:', error)
        }
        if (!mounted()) return
        const key = pageCacheKey(identity, pageNumber, scale)
        if (isCancellation && cancellationRetriedKeyRef.current !== key) {
          cancellationRetriedKeyRef.current = key
          setAttempt((count) => count + 1)
          return
        }
        setResult({
          key,
          imageUrl: null,
          width: 0,
          height: 0,
          isLoading: false,
          error: true,
        })
      } finally {
        releasePdfWork()
      }
    },
    [
      pageNumber,
      scale,
      totalPages,
      preloadAdjacentPages,
      releaseDocumentAfterRender,
    ]
  )

  useEffect(() => {
    let mounted = true

    if (!pdfIdentity || pageNumber > totalPages) {
      displayedUrlRef.current = null
      setResult(IDLE_PDF_IMAGE)
      return
    }

    if (!enabled) return

    const cacheKey = pageCacheKey(pdfIdentity, pageNumber, scale)
    const cached = pageCache.get(cacheKey)
    if (cached) {
      // Refresh LRU order
      pageCache.delete(cacheKey)
      pageCache.set(cacheKey, cached)
      displayedUrlRef.current = cached.blobUrl
      setResult({
        key: cacheKey,
        imageUrl: cached.blobUrl,
        width: cached.width,
        height: cached.height,
        isLoading: false,
        error: false,
      })
      return
    }

    setResult((prev) => ({
      ...prev,
      key: cacheKey,
      isLoading: true,
      error: false,
    }))

    const isDocSwitch = prevIdentityRef.current !== pdfIdentity
    prevIdentityRef.current = pdfIdentity

    if (isDocSwitch) {
      const timer = setTimeout(() => {
        loadPage(() => mounted, pdfIdentity)
      }, DOC_SWITCH_DEBOUNCE_MS)
      return () => {
        mounted = false
        clearTimeout(timer)
      }
    }

    loadPage(() => mounted, pdfIdentity)
    return () => {
      mounted = false
    }
  }, [pdfIdentity, pageNumber, scale, totalPages, loadPage, enabled, attempt])

  useEffect(() => {
    return () => {
      displayedUrlRef.current = null
    }
  }, [])

  const currentKey =
    pdfIdentity && pageNumber <= totalPages
      ? pageCacheKey(pdfIdentity, pageNumber, scale)
      : null
  currentKeyRef.current = currentKey
  return useMemo(
    () => ({ ...resolvePdfImageView(result, currentKey, enabled), reload }),
    [currentKey, enabled, reload, result]
  )
}
